class SireBuybox extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.busy = false;
    this.addEventListener('change', () => this.sync(), { signal: this.controller.signal });
    this.addEventListener('submit', (event) => this.add(event), { signal: this.controller.signal });
    this.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = input.defaultChecked; });
    this.classList.add('is-enhanced');
    this.sync();
    this.sticky = document.querySelector(`[data-sire-sticky-for="${this.dataset.section}"]`);
    this.sticky?.querySelector('[data-sire-sticky-add]')?.addEventListener('click', () => {
      const offer = this.querySelector('input[type="radio"]:checked')?.closest('.sire-offer');
      if (!offer || this.busy) return;
      if (offer.dataset.state === 'subscription_90') { this.scrollIntoView({behavior:'smooth', block:'center'}); return; }
      const form = offer.querySelector('form');
      const button = form?.querySelector('button[type="submit"]');
      if (button && !button.disabled) form.requestSubmit(button);
    }, {signal:this.controller.signal});
    if (this.sticky && typeof IntersectionObserver !== 'undefined') {
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.sticky.hidden = entry.isIntersecting;
      }, { threshold: 0 });
      this.visibilityObserver.observe(this.querySelector('input:checked')?.closest('.sire-offer')?.querySelector('button[type=submit]') || this);
    }
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.request?.abort();
    this.visibilityObserver?.disconnect();
    this.controller = null;
  }
  sync() {
    const selected = this.querySelector('input[type="radio"]:checked');
    if (!selected) return;
    const offer = selected.closest('.sire-offer');
    const sticky = document.querySelector(`[data-sire-sticky-for="${this.dataset.section}"]`);
    const summary = sticky?.querySelector('[data-sire-sticky-summary]');
    const billing = sticky?.querySelector('[data-sire-sticky-billing]');
    const stickyButton = sticky?.querySelector('[data-sire-sticky-add]');
    if (stickyButton) {
      stickyButton.textContent = offer.dataset.state === 'subscription_90' ? stickyButton.dataset.reviewLabel : stickyButton.dataset.addLabel;
      stickyButton.disabled = offer.querySelector('button[type="submit"]')?.dataset.available !== 'true';
    }
    if (this.visibilityObserver) { this.visibilityObserver.disconnect(); this.visibilityObserver.observe(offer.querySelector('button[type="submit"]')); }
    if (summary) summary.textContent = offer.dataset.summary;
    if (billing) billing.textContent = offer.dataset.billing;
    const productRoot = this.closest('[data-sire-product]');
    const openingPrice = productRoot?.querySelector('[data-sire-opening-price]');
    const openingTerms = productRoot?.querySelector('[data-sire-opening-terms]');
    if (openingPrice && offer.dataset.price) openingPrice.textContent = offer.dataset.price;
    if (openingTerms) openingTerms.textContent = `${offer.dataset.supply} · ${offer.dataset.billing}`;
    const gallery = this.closest('[data-sire-product]')?.querySelector('[data-sire-main-image]');
    this.closest('[data-sire-product]')?.querySelector('sire-gallery')?.showPanel?.('product');
    if (gallery && offer.dataset.image) {
      gallery.src = offer.dataset.image;
      if (offer.dataset.imageSrcset) gallery.srcset = offer.dataset.imageSrcset;
      else gallery.removeAttribute('srcset');
      gallery.alt = offer.dataset.imageAlt;
      const imageCaption = this.closest('[data-sire-product]')?.querySelector('[data-sire-image-caption]');
      if (imageCaption) imageCaption.textContent = offer.dataset.imageAlt;
      this.closest('[data-sire-product]')?.querySelectorAll('[data-sire-gallery-image]').forEach(button => {
        const matches = new URL(button.dataset.sireGalleryImage, window.location.href).pathname === new URL(offer.dataset.image, window.location.href).pathname;
        button.setAttribute('aria-pressed', String(matches));
      });
    }
    const caption = this.closest('[data-sire-product]')?.querySelector('[data-sire-supply-caption]');
    if (caption) caption.textContent = offer.dataset.supply;
    this.querySelectorAll('.sire-offer__form').forEach(form => {
      const active = form.closest('.sire-offer') === offer;
      form.querySelectorAll('input[name="id"], input[name="selling_plan"]').forEach(input => {
        input.disabled = !active;
      });
    });
    const error = this.querySelector('[data-sire-error]');
    if (error) error.textContent = '';
  }
  async add(event) {
    const form = event.target.closest('form');
    if (!form || !this.contains(form)) return;
    event.preventDefault();
    if (this.busy) return;
    const selected = this.querySelector('input[type="radio"]:checked')?.closest('.sire-offer');
    if (selected !== form.closest('.sire-offer')) return;
    const submit = form.querySelector('button[type="submit"]');
    if (submit.disabled) return;
    const inputs = new FormData(form);
    const id = inputs.get('id');
    const plan = inputs.get('selling_plan');
    if (!id) return;
    const item = { id, quantity: 1 };
    if (plan) item.selling_plan = plan;
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    this.querySelectorAll('button[type="submit"], input[type="radio"]').forEach(el => { el.disabled = true; });
    const stickyButton = this.sticky?.querySelector('[data-sire-sticky-add]');
    if (stickyButton) stickyButton.disabled = true;
    const original = submit.textContent;
    submit.textContent = this.dataset.adding;
    const error = this.querySelector('[data-sire-error]');
    error.textContent = '';
    this.request = new AbortController();
    const timeout = setTimeout(() => this.request.abort(), 15000);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const drawer = document.querySelector('cart-drawer');
      drawer?.setActiveElement(document.activeElement);
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [item], ...(drawer ? {sections: drawer.getSectionsToRender().map(section => section.id), sections_url: location.pathname} : {}) }), signal: this.request.signal
      });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.dataset.failure);
      const added = result.items?.[0];
      if (!added || String(added.variant_id || added.id) !== String(id)) throw new Error(this.dataset.uncertain);
      const addedPlan = added.selling_plan_allocation?.selling_plan?.id;
      if ((plan && String(addedPlan) !== String(plan)) || (!plan && addedPlan)) throw new Error(this.dataset.uncertain);
      const discount = plan ? inputs.get('sire_launch_discount') : null;
      if (!discount && drawer && result.sections?.['cart-drawer']) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(result);
      } else window.location.assign(discount ? `/discount/${encodeURIComponent(discount)}?redirect=${encodeURIComponent(`${root}cart`)}` : `${root}cart`);
    } catch (failure) {
      error.textContent = failure.name === 'AbortError' ? this.dataset.uncertain : (failure.message || this.dataset.failure);
      error.focus();
    } finally {
      clearTimeout(timeout);
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.querySelectorAll('button[type="submit"]').forEach(el => { el.disabled = el.dataset.available !== 'true'; });
      this.querySelectorAll('input[type="radio"]').forEach(el => { el.disabled = false; });
      submit.textContent = original;
      if (stickyButton) stickyButton.disabled = submit.disabled;
    }
  }
}
if (!customElements.get('sire-buybox')) customElements.define('sire-buybox', SireBuybox);
class SireGallery extends HTMLElement {
  showPanel(key) {
    this.querySelectorAll('[data-sire-gallery-panel]').forEach(panel => { panel.hidden = panel.dataset.sireGalleryPanel !== key; });
    this.querySelectorAll('[data-sire-gallery-target]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.sireGalleryTarget === key)));
    const buttons = [...this.querySelectorAll('[data-sire-gallery-target]')];
    const active = buttons.find(button => button.dataset.sireGalleryTarget === key);
    const rail = this.querySelector('.sire-gallery__nav');
    if (active && rail) { const delta = active.getBoundingClientRect().left - rail.getBoundingClientRect().left; if (delta < 0 || delta + active.offsetWidth > rail.clientWidth) rail.scrollLeft += delta; }
    const count = this.querySelector('[data-sire-gallery-count]');
    if (count) count.textContent = `${buttons.findIndex(button => button.dataset.sireGalleryTarget === key) + 1} / ${buttons.length}`;
  }
  move(step) {
    const buttons = [...this.querySelectorAll('[data-sire-gallery-target]')];
    const index = buttons.findIndex(button => button.getAttribute('aria-pressed') === 'true');
    if (buttons.length) this.showPanel(buttons[(index + step + buttons.length) % buttons.length].dataset.sireGalleryTarget);
  }
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.classList.add('is-enhanced');
    this.showPanel('product');
    const stage = this.querySelector('.sire-gallery__stage');
    stage?.addEventListener('touchstart', event => {
      this.touchStart = event.touches.length === 1 ? {x: event.touches[0].clientX, y: event.touches[0].clientY} : null;
    }, {passive: true, signal: this.controller.signal});
    stage?.addEventListener('touchend', event => {
      if (!this.touchStart || !event.changedTouches.length) return;
      const dx = event.changedTouches[0].clientX - this.touchStart.x;
      const dy = event.changedTouches[0].clientY - this.touchStart.y;
      this.touchStart = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.move(dx < 0 ? 1 : -1);
    }, {passive: true, signal: this.controller.signal});
    stage?.addEventListener('touchcancel', () => { this.touchStart = null; }, {signal: this.controller.signal});
    this.addEventListener('keydown', event => {
      const buttons = [...this.querySelectorAll('[data-sire-gallery-target]')];
      const index = buttons.indexOf(event.target);
      if (index < 0 || !['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
      this.showPanel(buttons[next].dataset.sireGalleryTarget);
      buttons[next].focus();
    }, { signal: this.controller.signal });
    this.addEventListener('click', event => {
      if (event.target.closest('[data-sire-label-view]')) { this.querySelector('.sire-label-dialog')?.showModal(); return; }
      if (event.target.closest('[data-sire-gallery-enlarge]')) {
        const source = this.querySelector('[data-sire-gallery-panel]:not([hidden]) img');
        const dialog = this.querySelector('.sire-gallery__dialog');
        if (source && dialog) {
          const image = dialog.querySelector('[data-sire-enlarged-image]');
          image.src = source.src;
          image.alt = source.alt;
          dialog.querySelector('[data-sire-enlarged-description]').textContent = source.alt;
          dialog.showModal();
        }
        return;
      }
      const stepButton = event.target.closest('[data-sire-gallery-step]');
      if (stepButton) { this.move(Number(stepButton.dataset.sireGalleryStep)); return; }
      const panelButton = event.target.closest('[data-sire-gallery-target]');
      if (panelButton) { this.showPanel(panelButton.dataset.sireGalleryTarget); return; }
      const button = event.target.closest('[data-sire-gallery-image]');
      if (!button) return;
      const image = this.querySelector('[data-sire-main-image]');
      image.src = button.dataset.sireGalleryImage;
      if (button.dataset.srcset) image.srcset = button.dataset.srcset;
      else image.removeAttribute('srcset');
      image.alt = button.dataset.alt;
      const caption = this.querySelector('[data-sire-image-caption]');
      if (caption) caption.textContent = button.dataset.caption || button.dataset.alt;
      this.querySelectorAll('[data-sire-gallery-image]').forEach(el => el.setAttribute('aria-pressed', String(el === button)));
    }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
}
if (!customElements.get('sire-gallery')) customElements.define('sire-gallery', SireGallery);

class SireNavigation extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const details = this.querySelector('details');
    const summary = details?.querySelector('summary');
    if (!details || !summary) return;
    this.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || !details.open) return;
      event.preventDefault();
      details.open = false;
      summary.focus();
    }, { signal: this.controller.signal });
    document.addEventListener('click', event => {
      if (details.open && !this.contains(event.target)) details.open = false;
    }, { signal: this.controller.signal });
    this.addEventListener('focusout', event => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) details.open = false;
    }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
}
if (!customElements.get('sire-navigation')) customElements.define('sire-navigation', SireNavigation);

class SireFormula extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    const nav = this.querySelector('[data-formula-nav]');
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = this.tabs.map(tab => this.querySelector(`[id="${tab.getAttribute('aria-controls')}"]`));
    if (!nav || !this.tabs.length || this.panels.some(panel => !panel)) return;
    this.controller = new AbortController();
    this.panels.forEach((panel, index) => {
      panel.open = true;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', this.tabs[index].id);
      panel.setAttribute('tabindex', '0');
    });
    this.setAttribute('data-enhanced', '');
    nav.hidden = false;
    this.select = this.querySelector('[data-formula-select]');
    const mobile = this.querySelector('[data-formula-mobile]');
    if (mobile) mobile.hidden = false;
    this.activate(0);
    this.select?.addEventListener('change', () => {
      const index = Number(this.select.value);
      if (Number.isInteger(index) && index >= 0 && index < this.tabs.length) this.activate(index);
    }, { signal: this.controller.signal });
    nav.addEventListener('click', event => {
      const tab = event.target.closest('[role="tab"]');
      if (this.tabs.includes(tab)) this.activate(this.tabs.indexOf(tab));
    }, { signal: this.controller.signal });
    nav.addEventListener('keydown', event => {
      const index = this.tabs.indexOf(event.target);
      if (index < 0) return;
      let next;
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;
      if (next === undefined) return;
      event.preventDefault();
      this.activate(next);
      this.tabs[next].focus();
    }, { signal: this.controller.signal });
  }
  activate(index) {
    if (this.select) this.select.value = String(index);
    this.tabs.forEach((tab, current) => {
      tab.setAttribute('aria-selected', String(current === index));
      tab.tabIndex = current === index ? 0 : -1;
      this.panels[current].hidden = current !== index;
    });
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
}
if (!customElements.get('sire-formula')) customElements.define('sire-formula', SireFormula);

document.querySelectorAll('[data-sire-countdown]').forEach(element => {
  const end = Date.parse(element.dataset.sireCountdown);
  if (!Number.isFinite(end)) return;
  const update = () => {
    const remaining = Math.max(0, end - Date.now());
    const target = element.querySelector('[data-countdown-value]');
    if (!remaining) { element.textContent = element.dataset.ended; clearInterval(timer); return; }
    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor(remaining / 3600000) % 24;
    const minutes = Math.floor(remaining / 60000) % 60;
    target.textContent = `${days}d ${hours}h ${minutes}m`;
  };
  const timer = setInterval(update, 60000);
  update();
});
document.querySelectorAll('[data-sire-share]').forEach(button => {
  button.addEventListener('click', async () => {
    const status = button.parentElement.querySelector('[data-share-status]');
    try {
      if (navigator.share) await navigator.share({title:button.dataset.title, url:button.dataset.url});
      else { await navigator.clipboard.writeText(button.dataset.url); status.textContent = button.dataset.copied; }
    } catch (error) {
      if (error.name !== 'AbortError') { status.textContent = button.dataset.url; }
    }
  });
});
